import React, { useCallback, useEffect, useState } from "react";
import { v4 as uuidV4 } from "uuid";

import { Col, Modal, notification, Radio, Row, Select, Spin } from "antd";
import PencilIcon from "../../../components/utils/icons/pencil";
import ButtonStandard from "../../../components/utils/button";
import { CardCustom, FarmDayScaleTreatmentParameterTable } from "./styles";
import { EditButton } from "../../reproductionParameters/cards/styles";
import { useSelector } from "react-redux";
import NumberFormat from "react-number-format";
import {
  findFarmReadFeederTreatmentParameterData,
  saveFarmReadFeederTreatmentParameterData,
} from "../../../services/farmReadFeederTreatmentParameterService";
import TrashIcon from "../../../components/utils/icons/trash";
import { sortReadingScale } from "../../../utils/readingScaleUtils";

// import { Container } from './styles';

const INITIAL_FORM_VALUES = {
  freeScale: false,
  scale: null,
  scalePercents: {},
  scalePercentsFree: [],
  readOnceADay: false,
  readAtEveryTreat: false,
  readInTreat: false,
  readTreats: [],
  readerReferenceType: null,
  applicationType: null,
  adjustmentType: "percent_original_matter",
};

const ADJUSTMENT_TYPES = [
  "percent_original_matter",
  "percent_dry_matter",
  "kg_dry_matter",
];

function FarmReadFeederTreatmentParameter() {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [errors, setErrors] = useState([]);

  const [form, setForm] = useState(INITIAL_FORM_VALUES);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const {
        data: { results },
      } = await findFarmReadFeederTreatmentParameterData({
        groupId,
        farmId,
      });
      const scalePercents = Object.entries(results.scalePercents).reduce(
        (acc, [key, value]) => ({ ...acc, [String(Number(key))]: value }),
        {}
      );
      const scalePercentsFree = Object.entries(scalePercents)
        .sort(([key1], [key2]) => sortReadingScale(Number(key1), Number(key2)))
        .map(([key, value]) => ({ id: uuidV4(), key: Number(key), value }));
      setForm({
        ...results,
        adjustmentType: results.adjustmentType || "percent_original_matter",
        scalePercents: scalePercents,
        scalePercentsFree: scalePercentsFree,
      });
    } catch (error) {
      notification.error({
        message: "Erro ao pegar os parâmetros de Leitura de Cocho",
      });
    } finally {
      setIsLoading(false);
    }
  }, [farmId, groupId]);

  const calculateAdjustBasedOnScale = useCallback(
    (maxAdjust, prevValue, scale) => {
      return scale > 0
        ? prevValue - maxAdjust / scale
        : (prevValue - maxAdjust / scale) * -1;
    },
    []
  );

  const validateForm = useCallback((data) => {
    let errorsArray = [];
    if (data.freeScale === false && (data.scale === null || data.scale === 0)) {
      errorsArray.push("scale");
      notification.error({
        message:
          "É necessário informar uma escala e especificar as porcentagens.",
      });
    }
    if (
      data.freeScale === false &&
      Object.entries(data.scalePercents).length > 0
    ) {
      const hasScalesWithoutPercents = Object.entries(data.scalePercents).some(
        ([key, value]) =>
          key === "0"
            ? value === null || value === undefined
            : value === null || value === undefined || value === 0
      );
      if (hasScalesWithoutPercents) {
        errorsArray.push("scale");
        notification.error({
          message:
            "É necessário informar uma porcentagem para cada escala definida.",
        });
      }
    }
    if (data.freeScale === true && data.scalePercentsFree.length > 0) {
      const hasScalesWithoutPercents = data.scalePercentsFree.some(
        ({ key, value }) =>
          value === null ||
          value === undefined ||
          key === null ||
          key === undefined ||
          key === ""
      );
      const hasScalesDuplicated = data.scalePercentsFree.reduce(
        (acc, { key }) => {
          if (key !== null && key !== undefined && key !== "") {
            acc[key] = (acc[key] || 0) + 1;
          }
          return acc;
        },
        {}
      );
      if (hasScalesWithoutPercents) {
        errorsArray.push("scale");
        notification.error({
          message:
            "É necessário informar uma porcentagem para cada escala definida.",
        });
      }
      if (Object.values(hasScalesDuplicated).some((count) => count > 1)) {
        errorsArray.push("scale");
        notification.error({
          message: "As escalas definidas não podem ser duplicadas.",
        });
      }
    }
    if (
      data.scalePercentsFree.length === 0 &&
      Object.entries(data.scalePercents).length === 0
    ) {
      errorsArray.push("scale");
      notification.error({
        message:
          "É necessário informar uma escala e especificar as porcentagens.",
      });
    }
    if (data.applicationType === null) {
      errorsArray.push("applicationType");
    }
    if (data.readerReferenceType === null) {
      errorsArray.push("readerReferenceType");
    }
    if (
      data.readAtEveryTreat === false &&
      data.readOnceADay === false &&
      data.readInTreat === false
    ) {
      errorsArray.push("readFrequency");
    }
    if (
      data.readInTreat === true &&
      (data.readTreats == null || data.readTreats.length === 0)
    ) {
      errorsArray.push("readFrequency");
    }

    setErrors(errorsArray);

    return errorsArray.length === 0;
  }, []);

  const handleAddNewScale = useCallback(() => {
    setForm((prev) => {
      return {
        ...prev,
        scalePercentsFree: [
          ...prev.scalePercentsFree,
          { id: uuidV4(), key: null, value: null },
        ],
      };
    });
  }, []);

  const handleRemovelScale = useCallback((id) => {
    setForm((prev) => {
      return {
        ...prev,
        scalePercentsFree: prev.scalePercentsFree.filter(
          (item) => item.id !== id
        ),
      };
    });
  }, []);

  const handleChangeScale = useCallback((scale) => {
    let scalePercents = [];
    for (let i = scale; i >= 0; i--) {
      scalePercents = {
        ...scalePercents,
        [i]: scalePercents[i] > 0 ? scalePercents[i] : 0,
      };
    }
    for (let i = 1; i <= scale; i++) {
      scalePercents = {
        ...scalePercents,
        [i * -1]: scalePercents[i * -1] < 0 ? scalePercents[i * -1] : 0,
      };
    }
    setForm((prev) => ({
      ...prev,
      scalePercents,
    }));
  }, []);

  const handleCompleteScalesBasedOnMaxAdjust = useCallback(() => {
    const scale = form.scale;
    const maxValue = form.scalePercents[scale];
    let prevValue = maxValue;

    let newPercents = {};

    if (maxValue && maxValue > 0) {
      Object.entries(form.scalePercents)
        .sort((entry1, entry2) => {
          const num1 = Number(entry1[0]);
          return num1 >= 0 ? -1 : 1; // Positives come before negatives
        })
        .forEach(([key, value], index) => {
          const numberKey = Number(key);
          if (index === 0) {
            newPercents = {
              ...newPercents,
              [key]: value,
            };
          } else if (index > 0 && numberKey > 0) {
            const newValue = calculateAdjustBasedOnScale(
              maxValue,
              prevValue,
              scale
            );
            prevValue = newValue;

            newPercents = {
              ...newPercents,
              [key]: newValue,
            };
          } else if (numberKey === 0) {
            newPercents = {
              ...newPercents,
              [key]: 0,
            };
          } else if (numberKey < 0) {
            newPercents = {
              ...newPercents,
              [key]: newPercents[String(numberKey * -1)],
            };
          }
        });
      setForm((prev) => ({
        ...prev,
        scalePercents: newPercents,
      }));
    }
  }, [calculateAdjustBasedOnScale, form.scale, form.scalePercents]);

  const isKgDryMatter = form.adjustmentType === "kg_dry_matter";

  const handleValidateScalePercentsValue = useCallback(
    (key, value, index) => {
      if (value > 25 && !isKgDryMatter) {
        Modal.confirm({
          centered: true,
          width: 600,
          content: (
            <h2>
              {translation.farmReadFeederTreatmentParameter.messages.percentAboveLimit.replace(
                "{{scale}}",
                key
              )}
            </h2>
          ),
          okText: translation.buttons.confirm,
          onOk: () => {
            if (index === 0) {
              handleCompleteScalesBasedOnMaxAdjust();
            }
          },
          onCancel: () => {
            setForm((prev) => ({
              ...prev,
              scalePercents: {
                ...prev.scalePercents,
                [key]: null,
              },
            }));
          },
        });
      } else if (index === 0) {
        handleCompleteScalesBasedOnMaxAdjust();
      }
      return;
    },
    [handleCompleteScalesBasedOnMaxAdjust, isKgDryMatter, translation]
  );

  const handleValidateFreeScalePercentsValue = useCallback(
    (key, value, index) => {
      if (value > 25 && !isKgDryMatter) {
        Modal.confirm({
          centered: true,
          width: 600,
          content: (
            <h2>
              {translation.farmReadFeederTreatmentParameter.messages.percentAboveLimit.replace(
                "{{scale}}",
                key
              )}
            </h2>
          ),
          okText: translation.buttons.confirm,
          onOk: () => {},
          onCancel: () => {
            setForm((prev) => ({
              ...prev,
              scalePercentsFree: prev.scalePercentsFree.map((item, itemIndex) =>
                itemIndex === index
                  ? {
                      ...item,
                      value: null,
                    }
                  : item
              ),
            }));
          },
        });
      } else if (index === 0) {
        handleCompleteScalesBasedOnMaxAdjust();
      }
      return;
    },
    [handleCompleteScalesBasedOnMaxAdjust, isKgDryMatter, translation]
  );

  const handleChangeAdjustmentType = useCallback(
    (adjustmentType) => {
      const applyChange = () =>
        setForm((prev) => ({
          ...prev,
          adjustmentType,
          scalePercents: Object.keys(prev.scalePercents).reduce(
            (acc, key) => ({ ...acc, [key]: key === "0" ? 0 : null }),
            {}
          ),
          scalePercentsFree: prev.scalePercentsFree.map((item) => ({
            ...item,
            value: null,
          })),
        }));
      const hasValues =
        Object.values(form.scalePercents).some((value) => value) ||
        form.scalePercentsFree.some(({ value }) => value);
      if (!hasValues) {
        applyChange();
        return;
      }
      Modal.confirm({
        centered: true,
        width: 600,
        content: (
          <h2>
            {
              translation.farmReadFeederTreatmentParameter.messages
                .confirmAdjustmentTypeChange
            }
          </h2>
        ),
        okText: translation.buttons.confirm,
        cancelText: translation.buttons.cancel,
        onOk: applyChange,
      });
    },
    [form.scalePercents, form.scalePercentsFree, translation]
  );

  async function handleSubmit() {
    const isFormValid = validateForm(form);
    if (isFormValid) {
      setIsLoading(true);
      let scalePercents = {};
      if (form.freeScale === false) {
        scalePercents = form.scalePercents;
      } else {
        scalePercents = form.scalePercentsFree.reduce((acc, item) => {
          const key = item.key !== null ? String(item.key) : "0";
          acc[key] = item.value !== null ? item.value : 0;
          return acc;
        }, {});
      }
      const body = {
        ...form,
        scalePercents: scalePercents,
      };
      delete body.scalePercentsFree;
      try {
        await saveFarmReadFeederTreatmentParameterData({
          groupId,
          farmId,
          body,
        });
        notification.success({
          message:
            translation.farmReadFeederTreatmentParameter.messages.successSave,
        });
        setIsEditing(false);
      } catch (error) {
        notification.error({
          message:
            translation.farmReadFeederTreatmentParameter.messages.errorSave,
        });
        console.error(
          "Error saving farm read feeder treatment parameters:",
          error
        );
      }
      setIsLoading(false);
    }
  }

  function handleCancel() {
    setIsEditing(false);
    setErrors([]);
    fetchData();
  }

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <CardCustom>
      <Spin spinning={isLoading}>
        <div className="body-container">
          <Row type="flex" justify="end" gutter={8}>
            {isEditing ? (
              <>
                <Col>
                  <ButtonStandard
                    type="submit"
                    name="buttonSaveFinancialTransactionsForm"
                    buttonType="principal"
                    onClick={handleSubmit}
                  >
                    {translation.buttons.save}
                  </ButtonStandard>
                </Col>
                <Col>
                  <ButtonStandard
                    name="buttonSaveFinancialTransactionsForm"
                    buttonType="secondary"
                    type="button"
                    onClick={handleCancel}
                  >
                    {translation.buttons.cancel}
                  </ButtonStandard>
                </Col>
              </>
            ) : (
              <Col>
                <EditButton
                  type="button"
                  onClick={() => setIsEditing((old) => !old)}
                  disabled={isEditing}
                >
                  {isEditing
                    ? `${translation.buttons.editing}...`
                    : `${translation.buttons.edit}`}
                  <PencilIcon />
                </EditButton>
              </Col>
            )}
          </Row>
          {/* Error message */}
          {errors.length > 0 && (
            <Row type="flex" justify="center" align="middle">
              <label className="error">{translation.error.formError}</label>
            </Row>
          )}
          <Row type="flex" gutter={32}>
            <Col span={8}>
              <Row
                type="flex"
                gutter={8}
                align="middle"
                style={{ marginBottom: 8 }}
              >
                <Col span={12}>
                  <label>
                    {
                      translation.farmReadFeederTreatmentParameter.form
                        .freeScale
                    }
                    *
                  </label>
                </Col>
                <Col span={12}>
                  <Row type="flex" gutter={8} align="middle">
                    <Col>
                      <Radio
                        disabled={!isEditing}
                        checked={form.freeScale === false}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            freeScale: false,
                            scalePercents: [],
                            scalePercentsFree: [],
                          }))
                        }
                      >
                        {
                          translation.farmReadFeederTreatmentParameter.form
                            .freeScaleOption.default
                        }
                      </Radio>
                    </Col>
                    <Col>
                      <Radio
                        disabled={!isEditing}
                        checked={form.freeScale === true}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            freeScale: true,
                            scale: null,
                            scalePercents: [],
                            scalePercentsFree: [],
                          }))
                        }
                      >
                        {
                          translation.farmReadFeederTreatmentParameter.form
                            .freeScaleOption.free
                        }
                      </Radio>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row type="flex" gutter={8} align="middle">
                {form.freeScale === false && (
                  <FarmDayScaleTreatmentParameterTable>
                    <tbody>
                      <tr>
                        <td>ESCALA</td>
                        <td className={isEditing ? "edit" : ""}>
                          <NumberFormat
                            disabled={!isEditing}
                            value={form.scale || ""}
                            decimalScale={0}
                            allowLeadingZeros={false}
                            allowNegative={false}
                            onValueChange={({ floatValue }) => {
                              setForm((prev) => ({
                                ...prev,
                                scale: floatValue,
                              }));
                            }}
                            onBlur={() => {
                              handleChangeScale(form.scale);
                            }}
                          />
                        </td>
                      </tr>
                      {Object.entries(form.scalePercents).length > 0 &&
                        Object.entries(form.scalePercents)
                          .sort((entry1, entry2) => {
                            const num1 = Number(entry1[0]);
                            return num1 >= 0 ? -1 : 1; // Positives come before negatives
                          })
                          .map(([scaleKey, scaleValue], index) => {
                            return (
                              <tr key={`${scaleKey}_${index}`}>
                                <td>{scaleKey}</td>
                                <td className={isEditing ? "edit" : ""}>
                                  <NumberFormat
                                    disabled={!isEditing}
                                    value={
                                      scaleValue === null ? "" : scaleValue
                                    }
                                    prefix={
                                      Number(scaleKey) > 0
                                        ? "+"
                                        : Number(scaleKey) < 0
                                        ? "-"
                                        : ""
                                    }
                                    suffix={isKgDryMatter ? " kg" : "%"}
                                    decimalScale={isKgDryMatter ? 1 : 0}
                                    decimalSeparator=","
                                    thousandSeparator={
                                      isKgDryMatter ? "." : undefined
                                    }
                                    allowLeadingZeros={true}
                                    allowNegative={false}
                                    onValueChange={({ floatValue }) => {
                                      const maxValue = isKgDryMatter
                                        ? 9999.9
                                        : 999;
                                      setForm((prev) => ({
                                        ...prev,
                                        scalePercents: {
                                          ...prev.scalePercents,
                                          [scaleKey]:
                                            floatValue > maxValue
                                              ? maxValue
                                              : floatValue,
                                        },
                                      }));
                                    }}
                                    onBlur={() => {
                                      handleValidateScalePercentsValue(
                                        scaleKey,
                                        scaleValue,
                                        index
                                      );
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                    </tbody>
                  </FarmDayScaleTreatmentParameterTable>
                )}
                {form.freeScale === true && (
                  <FarmDayScaleTreatmentParameterTable>
                    <tbody>
                      <tr>
                        <td colSpan={isEditing ? 3 : 2}>ESCALA</td>
                      </tr>
                      {isEditing && (
                        <tr>
                          <td colSpan={3}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                              }}
                            >
                              <ButtonStandard
                                type="button"
                                buttonType="secondary"
                                onClick={handleAddNewScale}
                              >
                                ADICIONAR ESCALA
                              </ButtonStandard>
                            </div>
                          </td>
                        </tr>
                      )}
                      {form.scalePercentsFree.map(
                        ({ id, key: scaleKey, value: scaleValue }, index) => {
                          return (
                            <tr key={id}>
                              <td className={isEditing ? "edit" : ""}>
                                <NumberFormat
                                  name={`scaleKey`}
                                  disabled={!isEditing}
                                  value={scaleKey ?? ""}
                                  decimalScale={1}
                                  decimalSeparator=","
                                  allowNegative
                                  onValueChange={({ floatValue }) => {
                                    setForm((prev) => ({
                                      ...prev,
                                      scalePercentsFree:
                                        prev.scalePercentsFree.map(
                                          (item, itemIndex) =>
                                            itemIndex === index
                                              ? {
                                                  ...item,
                                                  key:
                                                    floatValue === undefined
                                                      ? null
                                                      : Number(
                                                          floatValue.toFixed(1)
                                                        ),
                                                }
                                              : item
                                        ),
                                    }));
                                  }}
                                />
                              </td>
                              <td className={isEditing ? "edit" : ""}>
                                <NumberFormat
                                  name={`scaleValue`}
                                  disabled={!isEditing}
                                  value={scaleValue === null ? "" : scaleValue}
                                  suffix={isKgDryMatter ? " kg" : "%"}
                                  decimalScale={1}
                                  decimalSeparator=","
                                  thousandSeparator={
                                    isKgDryMatter ? "." : undefined
                                  }
                                  allowNegative
                                  onValueChange={({ floatValue }) => {
                                    const maxValue = isKgDryMatter
                                      ? 9999.9
                                      : 999;
                                    setForm((prev) => ({
                                      ...prev,
                                      scalePercentsFree:
                                        prev.scalePercentsFree.map(
                                          (item, itemIndex) =>
                                            itemIndex === index
                                              ? {
                                                  ...item,
                                                  value:
                                                    floatValue > maxValue
                                                      ? maxValue
                                                      : floatValue,
                                                }
                                              : item
                                        ),
                                    }));
                                  }}
                                  onBlur={() => {
                                    handleValidateFreeScalePercentsValue(
                                      scaleKey,
                                      scaleValue,
                                      index
                                    );
                                  }}
                                />
                              </td>
                              {isEditing && (
                                <td>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <ButtonStandard
                                      type="button"
                                      buttonType="type7"
                                      onClick={() => handleRemovelScale(id)}
                                    >
                                      <TrashIcon />
                                    </ButtonStandard>
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </FarmDayScaleTreatmentParameterTable>
                )}
              </Row>
            </Col>
            <Col className="body-container" span={16}>
              <Row type="flex" gutter={8} align="middle">
                <Col span={8}>
                  <label
                    className={errors.includes("readFrequency") ? "error" : ""}
                  >
                    {
                      translation.farmReadFeederTreatmentParameter.form
                        .readFrequency
                    }
                    *
                  </label>
                </Col>
                <Col>
                  <Row type="flex" gutter={8} align="middle">
                    <Col>
                      <Radio
                        disabled={!isEditing}
                        checked={form.readOnceADay}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            readOnceADay: !prev.readOnceADay,
                            readAtEveryTreat: false,
                            readInTreat: false,
                            readTreats: [],
                          }))
                        }
                      >
                        {
                          translation.farmReadFeederTreatmentParameter.form
                            .readFrequencyOption.readOnceADay
                        }
                      </Radio>
                    </Col>
                    <Col>
                      <Radio
                        disabled={!isEditing}
                        checked={form.readAtEveryTreat}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            readOnceADay: false,
                            readAtEveryTreat: !prev.readAtEveryTreat,
                            readInTreat: false,
                            readTreats: [],
                          }))
                        }
                      >
                        {
                          translation.farmReadFeederTreatmentParameter.form
                            .readFrequencyOption.readAtEveryTreat
                        }
                      </Radio>
                    </Col>
                    <Col>
                      <Radio
                        disabled={!isEditing}
                        checked={form.readInTreat}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            readOnceADay: false,
                            readAtEveryTreat: false,
                            readInTreat: !prev.readInTreat,
                            readTreats: [],
                          }))
                        }
                      >
                        {
                          translation.farmReadFeederTreatmentParameter.form
                            .readFrequencyOption.readInTreat
                        }
                      </Radio>
                    </Col>
                    {form.readInTreat && (
                      <Col>
                        <Select
                          disabled={!isEditing}
                          style={{ width: 200 }}
                          mode="multiple"
                          value={form.readTreats}
                          placeholder={translation.defaultSelectPlaceholder}
                          allowClear
                          onChange={(value) => {
                            setForm((prev) => ({
                              ...prev,
                              readTreats: value,
                            }));
                          }}
                        >
                          <Select.Option value={"T1"}>T1</Select.Option>
                          <Select.Option value={"T2"}>T2</Select.Option>
                          <Select.Option value={"T3"}>T3</Select.Option>
                          <Select.Option value={"T4"}>T4</Select.Option>
                          <Select.Option value={"T5"}>T5</Select.Option>
                          <Select.Option value={"T6"}>T6</Select.Option>
                          <Select.Option value={"T7"}>T7</Select.Option>
                          <Select.Option value={"T8"}>T8</Select.Option>
                        </Select>
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
              <Row type="flex" gutter={8} align="middle">
                <Col span={8}>
                  <label
                    className={
                      errors.includes("applicationType") ? "error" : ""
                    }
                  >
                    {
                      translation.farmReadFeederTreatmentParameter.form
                        .applicationType
                    }
                    *
                  </label>
                </Col>
                <Col>
                  <Row>
                    <Radio
                      disabled={!isEditing}
                      checked={
                        form.applicationType === "apply_equal_for_all_treats"
                      }
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          applicationType: "apply_equal_for_all_treats",
                        }))
                      }
                    >
                      {
                        translation.farmReadFeederTreatmentParameter.form
                          .applicationTypeOption.apply_equal_for_all_treats
                      }
                    </Radio>
                  </Row>
                  <Row>
                    <Radio
                      disabled={!isEditing}
                      checked={
                        form.applicationType === "concentrated_on_last_treat"
                      }
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          applicationType: "concentrated_on_last_treat",
                        }))
                      }
                    >
                      {
                        translation.farmReadFeederTreatmentParameter.form
                          .applicationTypeOption.concentrated_on_last_treat
                      }
                    </Radio>
                  </Row>
                </Col>
              </Row>
              <Row type="flex" gutter={8} align="middle">
                <Col span={8}>
                  <label>
                    {
                      translation.farmReadFeederTreatmentParameter.form
                        .adjustmentType
                    }
                    *
                  </label>
                </Col>
                <Col>
                  {ADJUSTMENT_TYPES.map((adjustmentType) => (
                    <Row key={adjustmentType}>
                      <Radio
                        disabled={!isEditing}
                        checked={form.adjustmentType === adjustmentType}
                        onChange={() =>
                          handleChangeAdjustmentType(adjustmentType)
                        }
                      >
                        {
                          translation.farmReadFeederTreatmentParameter.form
                            .adjustmentTypeOption[adjustmentType]
                        }
                      </Radio>
                    </Row>
                  ))}
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </Spin>
    </CardCustom>
  );
}

export default FarmReadFeederTreatmentParameter;
